import { useCallback, useState } from 'react';
import { useTranslation } from '../../../../../../../../../translation/TranslationContext';
import useOrderProductGridContext from '../../../../../contexts/OrderProductGrid.context';
import useHandleOrderProductPacks from '../../../../packsMenu/hooks/useHandleOrderProductPacks';
import { IPackToUpdate } from '../../../../packsMenu/types/useHandleOrderProductPacks.types';
import { getCommonGridFromSuggestedGrid } from '../../../utils/commonGrids.utils';
import { getPacksButtonLabel } from '../../../utils/productGrid.utils';

export const useOrderProductGridButtons = () => {
  const {
    orderId,
    product,
    readMode,
    hasValidPacks,
    selectedPacks,
    suggestedGrid,
    suggestedPacks,
    productSizeNames,
    showSuggestedGrid,
    suggestionAchieved,
    isOrderProductFromKit,
    handleSelectPacks,
    handleSelectSuggestion,
  } = useOrderProductGridContext();

  const { translate } = useTranslation();
  const [openPacksMenu, setOpenPacksMenu] = useState(false);
  const [openSelloutMenu, setOpenSelloutMenu] = useState(false);

  const { updatePacks } = useHandleOrderProductPacks({
    orderId,
    productColorId: product.productColorId,
    availability: product.availability,
    deliveryWindowId: product.deliveryWindowId,
  });

  const handleClickPacks = useCallback(() => {
    setOpenPacksMenu((prevOpen) => !prevOpen);
  }, []);

  const handleClosePacks = useCallback(() => {
    setOpenPacksMenu(false);
  }, []);

  const mustApplySuggestionOnClick = showSuggestedGrid && !suggestionAchieved;
  const noSuggestedPacks = suggestedPacks.length === 0;

  const showPacksButton =
    ((hasValidPacks && !readMode) || (hasValidPacks && readMode && selectedPacks.length > 0)) &&
    !isOrderProductFromKit;

  const disableUpdatePacksOnClick = !!handleSelectPacks;

  const handleClickApplySuggestion = useCallback(async () => {
    if (!mustApplySuggestionOnClick || readMode) {
      return;
    }
    const grid = getCommonGridFromSuggestedGrid(suggestedGrid, productSizeNames);
    if (grid && noSuggestedPacks && !disableUpdatePacksOnClick) {
      handleSelectSuggestion?.(grid);
      return;
    }
    if (grid && disableUpdatePacksOnClick) {
      handleSelectSuggestion?.(grid, suggestedPacks);
      return;
    }
    if (disableUpdatePacksOnClick || noSuggestedPacks) {
      return;
    }
    const packsToUpdate: IPackToUpdate[] = suggestedPacks.map((pack) => ({
      id: pack.packId,
      quantity: pack.quantity,
    }));
    await updatePacks(packsToUpdate);
  }, [
    readMode,
    updatePacks,
    productSizeNames,
    suggestedGrid,
    suggestedPacks,
    noSuggestedPacks,
    handleSelectSuggestion,
    disableUpdatePacksOnClick,
    mustApplySuggestionOnClick,
  ]);

  const packsButtonLabel = getPacksButtonLabel(selectedPacks, translate);

  const handleOpenSellout = () => {
    setOpenSelloutMenu(true);
  };

  const handleCloseSellout = () => {
    setOpenSelloutMenu(false);
  };

  return {
    openPacksMenu,
    openSelloutMenu,
    showPacksButton,
    packsButtonLabel,
    handleClickPacks,
    handleClosePacks,
    handleClickApplySuggestion,
    handleOpenSellout,
    handleCloseSellout,
  };
};
